import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    ReactNode,
} from "react"
import { getEntitlementUsage } from "../../services/subscription-service"

/**
 * Starea cotelor incluse ale abonamentului activ pe perioada curentă (mirror după
 * EntitlementUsageResponse din backend). `hasActivePlan=false` când nu există abonament.
 */
export interface EntitlementUsage {
    hasActivePlan: boolean
    tier?: string
    label?: string
    billingInterval?: string
    periodEnd?: string
    quotaResetAt?: string
    monthlyCreditStipend?: number | null
    includedJobPosts?: number | null
    includedJobPromotions?: number | null
    includedInvitations?: number | null
    includedNotifyCampaigns?: number | null
    includedEventCreates?: number | null
    usedJobPosts?: number
    usedJobPromotions?: number
    usedInvitations?: number
    usedNotifyCampaigns?: number
    usedEventCreates?: number
}

interface EntitlementsContextValue {
    /** Cotele curente, sau null dacă nu au fost încărcate încă. */
    usage: EntitlementUsage | null
    /** True cât timp e în curs prima încărcare. */
    loading: boolean
    /** Reîncarcă cotele din backend. Întoarce noua valoare (sau null la eroare). */
    refresh: () => Promise<EntitlementUsage | null>
    /**
     * După o abonare, planul se activează prin webhook Stripe (asincron), deci nu apare instant.
     * Reîncearcă câteva secunde până când un plan devine activ (sau se schimbă treapta/intervalul).
     */
    pollForActivation: (previous?: EntitlementUsage | null, attempts?: number) => void
}

const EntitlementsContext = createContext<EntitlementsContextValue>({
    usage: null,
    loading: true,
    refresh: async () => null,
    pollForActivation: () => {},
})

export const useEntitlements = () => useContext(EntitlementsContext)

// Semnătura după care decidem că starea abonamentului s-a schimbat (activare sau schimbare de plan).
const planSignature = (u: EntitlementUsage | null): string =>
    u ? `${u.hasActivePlan ? 1 : 0}:${u.tier ?? ""}:${u.billingInterval ?? ""}` : ""

export const EntitlementsProvider = ({ children }: { children: ReactNode }) => {
    const [usage, setUsage] = useState<EntitlementUsage | null>(null)
    const [loading, setLoading] = useState(true)
    const pollTimer = useRef<ReturnType<typeof setTimeout>>()

    const refresh = useCallback(async (): Promise<EntitlementUsage | null> => {
        if (!localStorage.getItem("jwtToken")) return null
        try {
            const u: any = await getEntitlementUsage()
            const next: EntitlementUsage = u ?? { hasActivePlan: false }
            setUsage(next)
            return next
        } catch {
            return null
        }
    }, [])

    const pollForActivation = useCallback(
        (previous?: EntitlementUsage | null, attempts = 8) => {
            if (pollTimer.current) clearTimeout(pollTimer.current)
            // Referința de la care pornim (dinainte de activare). Dacă nu o știm, o fixăm la primul
            // răspuns ca să nu oprim prematur.
            let baseline = previous !== undefined ? planSignature(previous) : planSignature(usage)
            let baselineKnown = previous !== undefined || usage != null
            let left = attempts
            const tick = async () => {
                const u = await refresh()
                left -= 1
                if (!baselineKnown && u != null) {
                    baseline = planSignature(u)
                    baselineKnown = true
                }
                const changed = u != null && baselineKnown && planSignature(u) !== baseline
                if (left > 0 && !changed) {
                    pollTimer.current = setTimeout(tick, 2500)
                }
            }
            pollTimer.current = setTimeout(tick, 1500)
        },
        [refresh, usage]
    )

    useEffect(() => {
        refresh().finally(() => setLoading(false))
        return () => {
            if (pollTimer.current) clearTimeout(pollTimer.current)
        }
    }, [refresh])

    return (
        <EntitlementsContext.Provider value={{ usage, loading, refresh, pollForActivation }}>
            {children}
        </EntitlementsContext.Provider>
    )
}
